import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Title,
  Text,
  Stack,
  Group,
  SimpleGrid,
  Button,
  Select,
  Textarea,
  Badge,
  Anchor,
  Box,
  Image,
} from "@mantine/core";
import { StatusBadge, PageLoader } from "@/components/common";
import { ApartmentDetailMap } from "@/components/apartments";
import { TransitTimeline } from "@/components/transit";
import { apartmentsApi, settingsApi } from "@/api/client";
import type { ApartmentStatus } from "@/api/types";

export const Route = createFileRoute("/_authenticated/apartments/$id")({
  component: ApartmentDetailPage,
});

const statusOptions = [
  { value: "new", label: "New" },
  { value: "stale", label: "Stale" },
  { value: "contacted", label: "Contacted" },
  { value: "applied", label: "Applied" },
  { value: "rejected", label: "Rejected" },
  { value: "ignored", label: "Ignored" },
];

function ApartmentDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");

  const { data: apartment, isLoading } = useQuery({
    queryKey: ["apartments", id],
    queryFn: () => apartmentsApi.get(Number(id)),
  });

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => settingsApi.get(),
  });

  useEffect(() => {
    if (apartment?.notes !== undefined) {
      setNotes(apartment.notes || "");
    }
  }, [apartment?.notes]);

  const statusMutation = useMutation({
    mutationFn: (status: ApartmentStatus) => apartmentsApi.updateStatus(Number(id), status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apartments", id] });
    },
  });

  const notesMutation = useMutation({
    mutationFn: (notes: string) => apartmentsApi.updateNotes(Number(id), notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apartments", id] });
    },
  });

  const officeCoords = useMemo(() => {
    if (!settings?.office_latitude || !settings?.office_longitude) {
      return null;
    }
    return {
      latitude: parseFloat(settings.office_latitude),
      longitude: parseFloat(settings.office_longitude),
    };
  }, [settings?.office_latitude, settings?.office_longitude]);

  if (isLoading || !apartment) {
    return <PageLoader />;
  }

  return (
    <Box>
      <Anchor
        component={Link}
        to="/apartments"
        search={{ status: undefined }}
        c="primary"
        mb="md"
        display="inline-block"
      >
        &larr; Back to apartments
      </Anchor>

      <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="lg">
        <Stack gap="lg" style={{ gridColumn: "span 2" }}>
          {/* Main Info */}
          <Paper withBorder p="lg" style={{ borderColor: "#0f3460" }}>
            <Group justify="space-between" align="flex-start" mb="md">
              <Box>
                <Title order={2}>{apartment.title}</Title>
                <Text c="dimmed" mt="xs">
                  {apartment.address}
                </Text>
                {apartment.is_corporate && (
                  <Badge color="blue" variant="light" mt="xs">
                    Corporate Rental
                  </Badge>
                )}
              </Box>
              <StatusBadge status={apartment.status} />
            </Group>

            {apartment.images && apartment.images.length > 0 && (
              <Group gap="sm" mb="lg" style={{ overflowX: "auto" }}>
                {apartment.images.slice(0, 5).map((img, idx) => (
                  <Image
                    key={idx}
                    src={img}
                    alt={`${apartment.title} ${idx + 1}`}
                    h={192}
                    radius="md"
                    fit="cover"
                  />
                ))}
              </Group>
            )}

            <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md" mb="lg">
              <Box>
                <Text c="dimmed" size="sm">
                  Rent
                </Text>
                <Text size="xl" fw={700} c="primary">
                  {apartment.rent.toLocaleString()} kr/mo
                </Text>
              </Box>
              {apartment.size_sqm && (
                <Box>
                  <Text c="dimmed" size="sm">
                    Size
                  </Text>
                  <Text size="xl" fw={700}>
                    {apartment.size_sqm} m²
                  </Text>
                </Box>
              )}
              {apartment.rooms && (
                <Box>
                  <Text c="dimmed" size="sm">
                    Rooms
                  </Text>
                  <Group gap="xs" align="center">
                    <Text size="xl" fw={700}>
                      {apartment.rooms}
                    </Text>
                    {apartment.is_first_hand && (
                      <Badge color="green" variant="light">
                        First Hand
                      </Badge>
                    )}
                  </Group>
                </Box>
              )}
              {apartment.floor && (
                <Box>
                  <Text c="dimmed" size="sm">
                    Floor
                  </Text>
                  <Text size="xl" fw={700}>
                    {apartment.floor}
                  </Text>
                </Box>
              )}
              {apartment.furnished && (
                <Box>
                  <Text c="dimmed" size="sm">
                    Furnished
                  </Text>
                  <Text size="xl" fw={700}>
                    {apartment.furnished === "yes"
                      ? "Yes"
                      : apartment.furnished === "no"
                        ? "No"
                        : apartment.furnished === "partial"
                          ? "Partially"
                          : apartment.furnished}
                  </Text>
                </Box>
              )}
            </SimpleGrid>

            {/* Rent Breakdown */}
            {(apartment.rent_base || apartment.deposit_months) && (
              <Paper
                p="md"
                mb="lg"
                radius="md"
                style={{ backgroundColor: "rgba(15, 52, 96, 0.5)" }}
              >
                <Text c="dimmed" size="sm" mb="sm" fw={500}>
                  Cost Breakdown
                </Text>
                <Stack gap="xs">
                  {apartment.rent_base && (
                    <Group justify="space-between">
                      <Text c="dimmed">Base rent</Text>
                      <Text>{apartment.rent_base.toLocaleString()} kr</Text>
                    </Group>
                  )}
                  {apartment.rent_service_fee && (
                    <Group justify="space-between">
                      <Text c="dimmed">Service fee</Text>
                      <Text>{apartment.rent_service_fee.toLocaleString()} kr</Text>
                    </Group>
                  )}
                  {apartment.rent_base && apartment.rent_service_fee && (
                    <Group
                      justify="space-between"
                      pt="xs"
                      style={{ borderTop: "1px solid #0f3460" }}
                    >
                      <Text fw={500}>Total</Text>
                      <Text c="primary" fw={500}>
                        {(apartment.rent_base + apartment.rent_service_fee).toLocaleString()} kr/mo
                      </Text>
                    </Group>
                  )}
                  {apartment.deposit_months && (
                    <Group
                      justify="space-between"
                      pt="xs"
                      style={{ borderTop: "1px solid #0f3460" }}
                    >
                      <Text c="dimmed">Deposit</Text>
                      <Group gap="xs">
                        <Text>
                          {apartment.deposit_months} month{apartment.deposit_months > 1 ? "s" : ""}
                        </Text>
                        {apartment.deposit_covered_by_qasa && (
                          <Badge color="green" variant="light" size="sm">
                            Covered by Qasa
                          </Badge>
                        )}
                      </Group>
                    </Group>
                  )}
                </Stack>

                {/* Additional costs */}
                {apartment.additional_costs &&
                  Object.keys(apartment.additional_costs).length > 0 && (
                    <Box mt="md" pt="sm" style={{ borderTop: "1px solid #0f3460" }}>
                      <Text c="dimmed" size="xs" mb="xs">
                        Additional Costs
                      </Text>
                      <SimpleGrid cols={2} spacing="xs">
                        {Object.entries(apartment.additional_costs).map(([key, value]) => (
                          <Group key={key} justify="space-between">
                            <Text c="dimmed" size="sm" tt="capitalize">
                              {key.replace(/_/g, " ")}
                            </Text>
                            <Text
                              size="sm"
                              c={
                                value === "included"
                                  ? "green"
                                  : value === "not_included"
                                    ? "dimmed"
                                    : undefined
                              }
                            >
                              {value === "included"
                                ? "Included"
                                : value === "not_included"
                                  ? "Not included"
                                  : `${value} kr`}
                            </Text>
                          </Group>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}
              </Paper>
            )}

            {apartment.description && (
              <Box mb="lg">
                <Text c="dimmed" size="sm" mb="xs">
                  Description
                </Text>
                <Text style={{ whiteSpace: "pre-wrap" }}>{apartment.description}</Text>
              </Box>
            )}

            <Group gap="md">
              <Button component="a" href={apartment.url} target="_blank" rel="noopener noreferrer">
                View on Qasa
              </Button>
              <Button
                component={Link}
                to="/messages/compose/$id"
                params={{ id: id }}
                variant="outline"
              >
                Compose Message
              </Button>
            </Group>
          </Paper>

          {/* Map */}
          {apartment.latitude && apartment.longitude && (
            <Paper withBorder p="lg" style={{ borderColor: "#0f3460" }}>
              <Title order={3} mb="md">
                Location
              </Title>
              <ApartmentDetailMap apartment={apartment} officeCoords={officeCoords} height={320} />
            </Paper>
          )}

          {/* Amenities */}
          {apartment.amenities &&
            Object.keys(apartment.amenities).filter((k) => k !== "furnished").length > 0 && (
              <Paper withBorder p="lg" style={{ borderColor: "#0f3460" }}>
                <Title order={3} mb="md">
                  Amenities
                </Title>
                <SimpleGrid cols={{ base: 2, md: 3 }} spacing="xs">
                  {Object.entries(apartment.amenities)
                    .filter(([key]) => key !== "furnished")
                    .map(([key, value]) => (
                      <Box
                        key={key}
                        px="sm"
                        py="xs"
                        style={{
                          borderRadius: "var(--mantine-radius-md)",
                          backgroundColor: value ? "rgba(64, 192, 87, 0.2)" : "#0f3460",
                          color: value
                            ? "var(--mantine-color-green-4)"
                            : "var(--mantine-color-dimmed)",
                        }}
                      >
                        <Text size="sm" tt="capitalize">
                          {key.replace(/_/g, " ")}
                        </Text>
                      </Box>
                    ))}
                </SimpleGrid>
              </Paper>
            )}

          {/* Price History */}
          {apartment.changes && apartment.changes.length > 0 && (
            <Paper withBorder p="lg" style={{ borderColor: "#0f3460" }}>
              <Title order={3} mb="md">
                Price History
              </Title>
              <Stack gap="xs">
                {apartment.changes
                  .filter((c) => c.field_name === "rent")
                  .map((change) => (
                    <Group
                      key={change.id}
                      justify="space-between"
                      py="xs"
                      style={{ borderBottom: "1px solid #0f3460" }}
                    >
                      <Text c="dimmed">{new Date(change.detected_at).toLocaleDateString()}</Text>
                      <Text>
                        {change.old_value} kr → {change.new_value} kr
                      </Text>
                    </Group>
                  ))}
              </Stack>
            </Paper>
          )}
        </Stack>

        <Stack gap="lg">
          {/* Status & Notes */}
          <Paper withBorder p="lg" style={{ borderColor: "#0f3460" }}>
            <Title order={3} mb="md">
              Status
            </Title>
            <Stack gap="md">
              <Select
                label="Update Status"
                value={apartment.status}
                onChange={(v) => v && statusMutation.mutate(v as ApartmentStatus)}
                data={statusOptions}
              />

              <Textarea
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this apartment..."
              />
              <Button
                size="sm"
                onClick={() => notesMutation.mutate(notes)}
                loading={notesMutation.isPending}
              >
                Save Notes
              </Button>
            </Stack>
          </Paper>

          {/* Transit Info */}
          {apartment.transit_info && (
            <TransitTimeline
              transitInfo={apartment.transit_info}
              officeAddress={apartment.office_address}
            />
          )}

          {/* Public Transport */}
          {apartment.public_transport && apartment.public_transport.length > 0 && (
            <Paper withBorder p="lg" style={{ borderColor: "#0f3460" }}>
              <Title order={3} mb="md">
                Nearby Transport
              </Title>
              <Stack gap="sm">
                {apartment.public_transport.map((stop, idx) => (
                  <Group
                    key={idx}
                    justify="space-between"
                    align="flex-start"
                    py="xs"
                    style={{
                      borderBottom:
                        idx < apartment.public_transport!.length - 1
                          ? "1px solid #0f3460"
                          : undefined,
                    }}
                  >
                    <Box>
                      <Group gap="xs">
                        {stop.type === "metro" ? (
                          <Box
                            w={20}
                            h={20}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "var(--mantine-color-red-6)",
                              color: "white",
                              fontSize: "var(--mantine-font-size-xs)",
                              fontWeight: 700,
                              borderRadius: "var(--mantine-radius-sm)",
                            }}
                          >
                            T
                          </Box>
                        ) : (
                          <Box
                            w={20}
                            h={20}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "var(--mantine-color-blue-6)",
                              color: "white",
                              fontSize: "var(--mantine-font-size-xs)",
                              fontWeight: 700,
                              borderRadius: "var(--mantine-radius-sm)",
                            }}
                          >
                            B
                          </Box>
                        )}
                        <Text fw={500}>{stop.name}</Text>
                      </Group>
                      {/* Show line numbers */}
                      {stop.lines && stop.lines.length > 0 && (
                        <Group gap={4} mt="xs" ml={28} wrap="wrap">
                          {stop.type === "metro" && stop.line_numbers
                            ? // Show metro line numbers (T14, T17, etc.)
                              stop.line_numbers.map((num) => (
                                <Badge key={num} color="red" variant="light" size="xs">
                                  T{num}
                                </Badge>
                              ))
                            : // Show bus line numbers
                              stop.lines.slice(0, 5).map((line, lineIdx) => (
                                <Badge key={lineIdx} color="blue" variant="light" size="xs">
                                  {line}
                                </Badge>
                              ))}
                          {stop.lines.length > 5 && stop.type === "bus" && (
                            <Text size="xs" c="dimmed">
                              +{stop.lines.length - 5} more
                            </Text>
                          )}
                        </Group>
                      )}
                    </Box>
                    {stop.distance_m && (
                      <Text c="dimmed" size="sm">
                        {stop.distance_m}m
                      </Text>
                    )}
                  </Group>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Matched Filters */}
          {apartment.matches && apartment.matches.length > 0 && (
            <Paper withBorder p="lg" style={{ borderColor: "#0f3460" }}>
              <Title order={3} mb="md">
                Matched Filters
              </Title>
              <Stack gap="xs">
                {apartment.matches.map((match) => (
                  <Box
                    key={match.id}
                    px="sm"
                    py="xs"
                    style={{ backgroundColor: "#0f3460", borderRadius: "var(--mantine-radius-md)" }}
                  >
                    <Text size="sm">{match.filter?.name || `Filter ${match.filter_id}`}</Text>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Messages */}
          {apartment.messages && apartment.messages.length > 0 && (
            <Paper withBorder p="lg" style={{ borderColor: "#0f3460" }}>
              <Title order={3} mb="md">
                Messages
              </Title>
              <Stack gap="sm">
                {apartment.messages.map((msg) => (
                  <Box
                    key={msg.id}
                    p="sm"
                    style={{ backgroundColor: "#0f3460", borderRadius: "var(--mantine-radius-md)" }}
                  >
                    <Group justify="space-between" mb="xs">
                      <Text size="sm" c="dimmed">
                        {msg.status}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </Text>
                    </Group>
                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                      {msg.content.substring(0, 100)}...
                    </Text>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
      </SimpleGrid>
    </Box>
  );
}
